import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Box, 
    Button, 
    TextField, 
    Typography, 
    Paper, 
    LinearProgress,
    Grid,
    Modal,
    Backdrop,  // Add this import
    CircularProgress,  // Add this import
    Stack, // Add Stack import
    Alert, // Add this import
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { alpha } from '@mui/material/styles';  // Add this import
import SearchIcon from '@mui/icons-material/Search';  // Use this instead of SecurityScan
import BugReportIcon from '@mui/icons-material/BugReport';  // Add this import
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import StorageIcon from '@mui/icons-material/Storage';
import ShieldIcon from '@mui/icons-material/Shield';
import axios from 'axios';
import wsService from '../../services/websocket';
import scanApi from '../../services/api';
import VulnerabilityDetails from '../VulnerabilityDetails';
import { useAuth } from '../../hooks/useAuth';  // Add this import
import UpgradeModal from '../common/UpgradeModal';  // Remove .jsx extension
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LoginRegisterModal from '../LoginRegisterModal';  // Add this import
import { keyframes } from '@mui/system';  // Add this import
import '@fontsource/roboto';  // Add this import for modern font
import '@fontsource/orbitron';  // Aggiungi import per Orbitron font
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessIcon from '@mui/icons-material/Business';
import { useNavigate } from 'react-router-dom';

// Define keyframes for the new animation
const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Aggiungi animazione lettera per lettera
const letterAnimation = keyframes`
  0% { 
    opacity: 0.2;
    text-shadow: none;
  }
  50% {
    opacity: 1;
    text-shadow: 0 0 10px #60A5FA, 0 0 20px #60A5FA;
  }
  100% {
    opacity: 0.2;
    text-shadow: none;
  }
`;

// Funzione per generare i delay delle lettere
const generateLetterStyles = (text) => {
    return text.split('').map((letter, i) => ({
        display: 'inline-block',
        animation: `${letterAnimation} 6s infinite`,
        animationDelay: `${i * 0.3}s`,
        opacity: 0.2
    }));
};

// Add new keyframes
const scannerGlow = keyframes`
  0% { filter: drop-shadow(0 0 5px #60A5FA); }
  50% { filter: drop-shadow(0 0 15px #60A5FA); }
  100% { filter: drop-shadow(0 0 5px #60A5FA); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Update API and WebSocket URLs to use environment variables
const API_URL = window.ENV?.apiUrl || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3000/ws';

let reconnectTimeout = null;

// Add status constants at the top of file
const SCAN_STATUSES = {
    SCHEDULED: 'scheduled',
    QUEUED: 'queued',
    STARTING: 'starting', 
    PROCESSING: 'processing',
    ABORTING: 'aborting',
    ABORTED: 'aborted',
    PAUSING: 'pausing',
    PAUSED: 'paused',
    COMPLETED: 'completed',
    FAILED: 'failed'
};

// Aggiorna la lista degli stati terminali in cima al file
const TERMINAL_STATUSES = [
    SCAN_STATUSES.COMPLETED,
    SCAN_STATUSES.FAILED,
    SCAN_STATUSES.ABORTED,
    SCAN_STATUSES.ABORTING,  // Aggiungi aborting come stato terminale
    SCAN_STATUSES.PAUSED
];

const Scanner = ({ scanId: initialScanId }) => {
    const [targetUrl, setTargetUrl] = useState('');
    const [currentScan, setCurrentScan] = useState(null);
    const [progress, setProgress] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState('initializing');
    const [connectionError, setConnectionError] = useState(null);
    const [scanStatus, setScanStatus] = useState('');
    const [scanError, setScanError] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [persistentState] = useState(() => {
        const saved = sessionStorage.getItem('scannerState');
        return saved ? JSON.parse(saved) : null;
    });
    const [isInitialized, setIsInitialized] = useState(false);
    const lastStatusRef = useRef(null);
    const lastProgressRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [vulnerabilities, setVulnerabilities] = useState([]);
    const scanIntervalRef = useRef(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [vulnId, setVulnId] = useState(null);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);  // Add this state
    const { quotaInfo } = useAuth();  // Add this state
    const [isResuming, setIsResuming] = useState(false);  // Add this state
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [scheduledTime, setScheduledTime] = useState(null);
    const [success, setSuccess] = useState(null); // Add success state
    const [error, setError] = useState(null); // Add error state
    const [showDomainVerificationModal, setShowDomainVerificationModal] = useState(false);
    const [domainToVerify, setDomainToVerify] = useState('');
    const [reports, setReports] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportStates, setReportStates] = useState({});
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [groupedReports, setGroupedReports] = useState({});
    const [selectedTargetUrl, setSelectedTargetUrl] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const navigate = useNavigate();

    // Add state management optimization
    const [scanData, setScanData] = useState({
        stats: null,
        vulnerabilities: [],
        status: '',
        progress: 0
    });

    // Add debounce utility
    const debouncedUpdateState = useCallback(
        debounce((newData) => {
            setScanData(prev => ({...prev, ...newData}));
        }, 1000),
        []
    );

    // Initialize state with persistent data
    useEffect(() => {
        if (persistentState) {
            setCurrentScan(persistentState.currentScan);
            setProgress(persistentState.progress);
        }
    }, [persistentState]);

    // Save state changes to session storage
    useEffect(() => {
        if (currentScan) {
            const stateToSave = {
                currentScan,
                scanStatus,
                progress
            };
            sessionStorage.setItem('scannerState', JSON.stringify(stateToSave));
        } else {
            sessionStorage.removeItem('scannerState');
        }
    }, [currentScan, scanStatus, progress]);

    // Update handleScanUpdate with optimized state management
    const handleScanUpdate = useCallback((scanData) => {
        if (!scanData?.data) return;
        
        const data = scanData.data;
        console.log('Processing scan update:', data);

        // Use single state update for all changes
        const newState = {};

        // Update scan status and progress
        if (data.status) {
            newState.status = data.status;
            lastStatusRef.current = data.status;
        }

        // Update scan statistics
        if (data.statistics) {
            const stats = data.statistics;
            newState.progress = stats.progress || 0;
            newState.stats = {
                requestCount: stats.requestCount || 0,
                avgResponseTime: stats.avgResponseTime || 0,
                locations: stats.locations || 1,
                progress: stats.progress || 0,
                status: stats.status || data.status
            };
        }

        // Update vulnerabilities only if changed
        if (data.vulnerabilities?.length > 0) {
            const vulnMap = new Map(data.vulnerabilities.map(v => [v.vuln_id || v.id, v]));
            data.vulnerabilities.forEach(vuln => {
                const vulnId = vuln.vuln_id || vuln.id;
                if (vulnId) vulnMap.set(vulnId, vuln);
            });
            newState.vulnerabilities = Array.from(vulnMap.values());
        }

        if (Object.keys(newState).length > 0) {
            debouncedUpdateState(newState);
        }

        if (['completed', 'failed', 'aborted'].includes(data.status)) {
            setIsCompleted(true);
        }
    }, []);

    // Add cleanup function reference
    const cleanupRef = useRef(null);

    // Update WebSocket effect with better cleanup
    useEffect(() => {
        if (!isInitialized) {
            console.log('Initializing WebSocket connection...');

            const cleanup = () => {
                if (wsService.isConnected()) {
                    console.log('Cleaning up WebSocket connection...');
                    wsService.unsubscribeAll();
                    wsService.cleanup();
                }
                if (reconnectTimeout) {
                    clearTimeout(reconnectTimeout);
                }
            };

            wsService.setConnectionHandler((status) => {
                console.log('WebSocket connection status changed:', status);
                setConnectionStatus(status);
                if (status === 'error') {
                    setConnectionError('Failed to establish WebSocket connection');
                    if (reconnectTimeout) clearTimeout(reconnectTimeout);
                    reconnectTimeout = setTimeout(() => {
                        wsService.connect(WS_URL);
                    }, 5000);
                } else {
                    setConnectionError(null);
                }
            });

            wsService.setMessageCallback((message) => {
                if (document.visibilityState === 'visible') {
                    handleScanUpdate(message);
                }
            });

            if (!wsService.isConnected()) {
                console.log('Initiating WebSocket connection...');
                wsService.connect(WS_URL);
            } else if (currentScan?.scanId) {
                wsService.subscribeScan(currentScan.scanId);
            }

            setIsInitialized(true);

            return cleanup;
        }
    }, [isInitialized, handleScanUpdate]);

    // Add visibility change handler
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                // Clean up when page is hidden
                if (cleanupRef.current) {
                    cleanupRef.current();
                }
            } else if (document.visibilityState === 'visible' && currentScan?.scanId) {
                // Reconnect when page becomes visible again and we have a scan
                wsService.connect(WS_URL);
                wsService.subscribeScan(currentScan.scanId);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [currentScan]);

    // Add token check on component mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setToken(token);
            setIsAuthenticated(true);
            // Add token to axios defaults
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }, []);

    // Remove redundant polling effects and use only WebSocket updates
    useEffect(() => {
        if (!currentScan?.scanId) return;

        // Initial data fetch
        const fetchInitialData = async () => {
            try {
                const [vulnData, statsData] = await Promise.all([
                    scanApi.getVulnerabilities(currentScan.scanId),
                    scanApi.getStatistics(currentScan.scanId)
                ]);

                if (vulnData) {
                    setVulnerabilities(vulnData);
                }
                
                if (statsData) {
                    setScanData(prev => ({
                        ...prev,
                        stats: statsData,
                        progress: statsData.progress || 0,
                        status: statsData.status || prev.status
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch initial scan data:', error);
            }
        };

        fetchInitialData();

        // Subscribe to WebSocket updates
        if (wsService.isConnected()) {
            wsService.subscribeScan(currentScan.scanId);
        }

        return () => {
            if (wsService.isConnected()) {
                wsService.unsubscribeScan(currentScan.scanId);
            }
        };
    }, [currentScan?.scanId]);

    // Update loadScan logic
    const loadScan = useCallback(async (id) => {
        try {
            setIsLoading(true);
            const isFirstLoad = !currentScan;
            if (isFirstLoad) {
                setIsResuming(true);
            }
            setScanError(null);
            
            const scanData = await scanApi.getScan(id);
            console.log('Loaded scan data:', scanData);

            if (scanData) {
                const isComplete = ['completed', 'failed', 'aborted'].includes(scanData.status);
                
                setCurrentScan({
                    scanId: id,
                    targetUrl: scanData.stats.targetUrl,
                    status: scanData.status,
                    stats: scanData.stats
                });
                setScanStatus(scanData.status);
                setProgress(scanData.stats.progress || 0);
                setIsCompleted(isComplete);
                setVulnerabilities(scanData.vulnerabilities || []);
                
                // Imposta sempre il targetUrl dai dati della scansione
                if (scanData.stats.targetUrl) {
                    setTargetUrl(scanData.stats.targetUrl);
                }
            }
        } catch (error) {
            console.error('Failed to load scan:', error);
            setScanError('Failed to load scan data');
        } finally {
            setIsLoading(false);
            setIsResuming(false);
        }
    }, [currentScan]); // Rimuovi targetUrl dalle dipendenze

    // Add this function to extract scanId from URL
    const getScanIdFromUrl = useCallback(() => {
        const pathParts = window.location.pathname.split('/');
        return pathParts[pathParts.indexOf('scan') + 1];
    }, []);

    // Add new effect to handle URL without scanId
    useEffect(() => {
        const path = window.location.pathname;
        if (path === '/scan') {
            // Reset all states when on base scan URL
            setCurrentScan(null);
            setScanStatus('');
            setProgress(0);
            setVulnerabilities([]);
            setIsCompleted(false);
            // Rimuovere questa linea: setTargetUrl('');
            setScanError(null);
            setIsResuming(false);
            
            // Clear session storage
            sessionStorage.removeItem('scannerState');
        }
    }, []);

    // Update initialization effect
    useEffect(() => {
        const scanIdToLoad = initialScanId || getScanIdFromUrl();
        if (scanIdToLoad) {
            loadScan(scanIdToLoad);
        } else {
            // Reset states when no scanId is provided
            setCurrentScan(null);
            setScanStatus('');
            setProgress(0);
            setVulnerabilities([]);
            setIsCompleted(false);
            // Rimuovere questa linea: setTargetUrl('');
            setScanError(null);
            setIsResuming(false);
        }
    }, [initialScanId, getScanIdFromUrl, loadScan]);

    // Remove duplicate loadScan effect
    useEffect(() => {
        const pollInterval = setInterval(() => {
            const scanId = initialScanId || currentScan?.scanId;
            if (scanId && !isCompleted) {
                loadScan(scanId);
            }
        }, 5000);
        
        return () => clearInterval(pollInterval);
    }, [initialScanId, currentScan?.scanId, isCompleted, loadScan]);

    // Rimuovere il reset del targetUrl dall'effetto di scan completion
    useEffect(() => {
        if (['completed', 'failed', 'aborted'].includes(scanStatus)) {
            setIsCompleted(true);
            setCurrentScan(prev => 
                prev ? {
                    ...prev,
                    status: scanStatus
                } : null
            );
        }
    }, [scanStatus, progress, initialScanId]);

    const checkDomainVerification = async (domain) => {
        try {
            const response = await axios.get(`${API_URL}/domains/verified`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            const verifiedDomains = response.data.domains || [];
            const urlObj = new URL(domain);
            // Estrai il dominio base rimuovendo www. e prendendo solo il dominio principale
            const domainToCheck = urlObj.hostname.replace(/^www\./, '');
            
            // Verifica se il dominio base è presente nella lista dei domini verificati
            return verifiedDomains.some(d => {
                const verifiedDomain = d.domain.replace(/^www\./, '');
                // Se il dominio da verificare contiene il dominio verificato, è valido
                return d.status === 'verified' && domainToCheck.includes(verifiedDomain);
            });
        } catch (error) {
            console.error('Error checking domain verification:', error);
            return false;
        }
    };

    // Modifica la funzione startScan
    const startScan = async () => {
        try {
            if (!targetUrl) {
                setError('Please enter a target URL');
                return;
            }

            let formattedUrl = targetUrl.trim();
            if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
                formattedUrl = 'https://' + formattedUrl;
            }

            try {
                new URL(formattedUrl);
            } catch (_) {
                setError('Please enter a valid URL');
                return;
            }

            // Verifica se il dominio è verificato
            const isVerified = await checkDomainVerification(formattedUrl);
            if (!isVerified) {
                setDomainToVerify(formattedUrl);
                setShowDomainVerificationModal(true);
                return;
            }

            setIsLoading(true);
            setError(null);
            setScanError(null);

            const response = await scanApi.startScan(formattedUrl);

            if (response?.scanId) {
                setIsCompleted(false);
                setProgress(0);
                setVulnerabilities([]);
                
                const newScan = { 
                    scanId: response.scanId, 
                    targetUrl: formattedUrl,
                    status: 'initializing',
                    stats: { progress: 0 }
                };
                setCurrentScan(newScan);
                setScanStatus('initializing');
                setTargetUrl(formattedUrl);
                
                window.history.pushState({}, '', `/scan/${response.scanId}`);
                wsService.subscribeScan(response.scanId);
                
                setSuccess('Scan started successfully');
            }
        } catch (error) {
            console.error('Scan creation failed:', error);
            const errorMessage = error.response?.data?.details || 
                               error.response?.data?.error || 
                               error.message;
            setError(`Failed to start scan: ${errorMessage}`);
            
            if (error.response?.status === 403 && error.response?.data?.error === 'Scan quota exceeded') {
                setUpgradeModalOpen(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Rimuovere reset targetUrl da stopScan
    const stopScan = async () => {
        if (!currentScan) return;

        try {
            await scanApi.stopScan(currentScan.scanId);
            
            setIsCompleted(true);
            setScanStatus('completed');
            setCurrentScan(prev => ({
                ...prev,
                status: 'completed',
                stats: {
                    ...prev.stats,
                    status: 'completed'
                }
            }));
            
            const finalVulns = await scanApi.getVulnerabilities(currentScan.scanId);
            if (finalVulns) {
                setVulnerabilities(finalVulns);
            }
        } catch (error) {
            console.error('Failed to stop scan:', error);
            setScanError('Failed to stop scan. Please try again.');
        }
    };

    const handleLogin = async (formData) => {
        try {
            setIsLoading(true);
            setAuthError(null);
            
            const response = await scanApi.login(formData);
            return response;
        } catch (error) {
            console.error('Login error:', error);
            if (error.message === 'ACCOUNT_PENDING_APPROVAL') {
                setAuthError('This account is pending administrator approval');
            } else {
                setAuthError(error.response?.data?.error || 'Login failed');
            }
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (formData) => {
        try {
            setIsLoading(true);
            setAuthError(null);
    
            const response = await axios.post(`${API_URL}/api/register`, formData, {
                headers: {
                    'Content-Type': 'application/json',
                },
                withCredentials: true
            });
    
            if (response.data?.requiresApproval) {
                // Dopo la registrazione, mostra il login
                setShowLoginModal(true);
                return response.data;
            }
            
        } catch (error) {
            console.error('Registration failed:', error);
            setAuthError(error.response?.data?.error || 'Registration failed');
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        scanApi.logout(); // Usa il metodo logout di scanApi invece della logica locale
    };

    // eslint-disable-next-line no-unused-vars
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2
    };

    // Update the progress display in the UI
    const renderScanStatus = () => {
        if (!currentScan) return null;
        
        const stats = currentScan?.stats || {};
        
        return (
            <Box sx={{ mt: 2 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                }}>
                    <Typography variant="body2">
                        Status: {scanStatus || 'Initializing...'}
                    </Typography>
                    <Typography variant="body2">
                        Progress: {progress || 0}%
                    </Typography>
                </Box>

                {currentScan && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 3,
                        my: 1,
                        p: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 1
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            Requests: {stats.requestCount || 0}/{Math.max(stats.locations || 1, 1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Avg Response Time: {stats.avgResponseTime || 0}ms
                        </Typography>
                    </Box>
                )}

                {scanError && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {scanError}
                    </Typography>
                )}
            </Box>
        );
    };

    // Render connection status
    const renderScannerHeader = () => (
        <Box
            sx={{
                mb: 4,
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(180deg, rgba(96, 165, 250, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%)',
                border: '1px solid',
                borderColor: 'divider',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Background animated elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    opacity: 0.1,
                    background: `
                        linear-gradient(90deg, #60A5FA 25%, transparent 25%) 10px 0,
                        linear-gradient(90deg, transparent 75%, #60A5FA 75%) 10px 0,
                        linear-gradient(0deg, #60A5FA 25%, transparent 25%) 0 10px,
                        linear-gradient(0deg, transparent 75%, #60A5FA 75%) 0 10px
                    `,
                    backgroundSize: '20px 20px',
                    animation: `${rotate} 60s linear infinite`,
                }}
            />

            <Stack 
                direction="row" 
                spacing={3} 
                alignItems="center"
                sx={{ position: 'relative', zIndex: 1 }}
            >
                <Box
                    sx={{
                        bgcolor: 'primary.main',
                        borderRadius: 2,
                        p: 2,
                        animation: `${scannerGlow} 3s ease-in-out infinite`,
                    }}
                >
                    <ShieldIcon 
                        sx={{ 
                            fontSize: 40,
                            color: 'white'
                        }} 
                    />
                </Box>

                <Stack spacing={1}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            fontWeight: 700,
                            fontFamily: '"Orbitron", sans-serif',
                            color: '#60A5FA',
                            textShadow: '0 0 10px rgba(96, 165, 250, 0.3)'
                        }}
                    >
                        Enterprise Security Scanner
                    </Typography>
                    <Typography 
                        variant="subtitle1" 
                        color="text.secondary"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontFamily: '"Roboto", sans-serif'
                        }}
                    >
                        <VpnLockIcon sx={{ fontSize: 20 }} />
                        Advanced Vulnerability Detection & Analysis
                    </Typography>
                </Stack>

                {quotaInfo && (
                    <Box 
                        sx={{ 
                            ml: 'auto',
                            p: 2,
                            borderLeft: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Typography variant="overline" color="text.secondary">
                            Scan Credits
                        </Typography>
                        <Typography variant="h6" color="primary">
                            {quotaInfo.remaining}/{quotaInfo.total}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Box>
    );

    const renderContent = () => (
        <Box sx={{ p: 3 }}>
            {success && (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mb: 2,
                        backgroundColor: 'rgba(46, 125, 50, 0.1)',
                        borderLeft: '4px solid #2e7d32'
                    }}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}
            <Paper 
                elevation={0}
                sx={{ 
                    background: 'linear-gradient(180deg, rgba(10, 20, 41, 0.95) 0%, rgba(14, 25, 48, 0.97) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid',
                    borderColor: 'rgba(96, 165, 250, 0.1)',
                    borderRadius: 3,
                    p: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.3), transparent)'
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.1), transparent)'
                    }
                }}
            >
                {renderScannerHeader()}
                
                {/* Input di scansione migliorato */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2,
                    flexDirection: { xs: 'column', md: 'row' },
                    mt: 4,
                    backgroundColor: 'rgba(17, 25, 45, 0.7)',
                    borderRadius: 2,
                    p: 3,
                    border: '1px solid',
                    borderColor: 'rgba(96, 165, 250, 0.1)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'radial-gradient(circle at top right, rgba(96, 165, 250, 0.03), transparent 70%)',
                        pointerEvents: 'none'
                    }
                }}>
                    <TextField
                        fullWidth
                        label="Target URL"
                        variant="outlined"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                        disabled={currentScan && !isCompleted && !['completed', 'failed', 'aborted'].includes(scanStatus)}
                        placeholder="Enter website URL to scan"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(19, 47, 76, 0.4)',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(19, 47, 76, 0.5)',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: 'rgba(19, 47, 76, 0.6)',
                                    '& fieldset': {
                                        borderColor: '#60A5FA !important',
                                        borderWidth: '2px !important'
                                    }
                                },
                                '& fieldset': {
                                    borderColor: 'rgba(96, 165, 250, 0.2)',
                                },
                                '& input': {
                                    color: 'rgb(255, 255, 255)',
                                    '&::placeholder': {
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        opacity: 1
                                    }
                                }
                            },
                            '& label': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&.Mui-focused': {
                                    color: '#60A5FA'
                                }
                            }
                        }}
                    />
                    {renderScanButton()}
                </Box>

                {/* Stato della scansione migliorato */}
                {currentScan && (
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: 4,
                            mt: 4,
                            borderRadius: 3,
                            backgroundColor: 'rgba(17, 25, 45, 0.7)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid',
                            borderColor: 'rgba(96, 165, 250, 0.1)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                background: 'radial-gradient(circle at top left, rgba(96, 165, 250, 0.03), transparent 70%)',
                                pointerEvents: 'none'
                            }
                        }}
                    >
                        {renderScanStatus()}
                    </Paper>
                )}

                {/* Vulnerabilità trovate */}
                {vulnerabilities.length > 0 && (
                    <Box sx={{ 
                        mt: 4,
                        p: 3,
                        borderRadius: 3,
                        backgroundColor: 'rgba(17, 25, 45, 0.7)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid',
                        borderColor: 'rgba(96, 165, 250, 0.1)',
                        position: 'relative'
                    }}>
                        <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ 
                                mb: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2 
                            }}
                        >
                            <BugReportIcon color="error" />
                            Vulnerabilities Found: {vulnerabilities.length}
                        </Typography>
                        <Grid container spacing={3}>
                            {vulnerabilities.map((vulnerability) => (
                                <Grid item xs={12} key={vulnerability.vuln_id || vulnerability.id}>
                                    {vulnerability && (
                                        <VulnerabilityDetails 
                                            vulnerability={vulnerability} 
                                            key={vulnerability.vuln_id || vulnerability.id}
                                        />
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Paper>
        </Box>
    );

    // Updated renderScanButton function with correct button text
    const renderScanButton = () => {
        // Considera uno scan attivo solo se non è in uno stato terminale
        const isScanning = currentScan && !isCompleted && !TERMINAL_STATUSES.includes(scanStatus);
        
        // Disabilita il bottone durante l'aborting
        const isDisabled = scanStatus === SCAN_STATUSES.ABORTING;
        
        return (
            <Stack direction="row" spacing={1}>
                <Button
                    variant="contained"
                    onClick={isScanning ? stopScan : startScan}
                    disabled={isDisabled}
                    color={isScanning ? "error" : "primary"}
                    sx={{ 
                        minWidth: 150,
                        position: 'relative',
                        gap: 1
                    }}
                >
                    {isScanning && <CircularProgress size={20} sx={{position: 'absolute', left: 10}} />}
                    <span style={{marginLeft: isScanning ? '24px' : '0'}}>
                        {scanStatus === SCAN_STATUSES.ABORTING ? 'Stopping...' : isScanning ? 'Stop Scan' : 'Start Scan'}
                    </span>
                </Button>
                
                <Button
                    variant="outlined"
                    onClick={() => setShowScheduleDialog(true)}
                    startIcon={<CalendarTodayIcon />}
                    disabled={isScanning}
                >
                    Schedule
                </Button>
            </Stack>
        );
    };

    // Add schedule dialog
    const renderScheduleDialog = () => (
        <Modal
            open={showScheduleDialog}
            onClose={() => setShowScheduleDialog(false)}
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2
            }}>
                <Typography variant="h6" gutterBottom>
                    Schedule Scan
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                        label="Schedule Time"
                        value={scheduledTime}
                        onChange={setScheduledTime}
                        slotProps={{ 
                            textField: { 
                                fullWidth: true,
                                variant: 'outlined'
                            } 
                        }}
                        minDateTime={new Date()}
                    />
                    <Button
                        variant="contained"
                        onClick={scheduleNewScan}
                        disabled={!scheduledTime}
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        Schedule Scan
                    </Button>
                </LocalizationProvider>
            </Box>
        </Modal>
    );

    // Add this new function to handle scheduled scans
    const scheduleNewScan = async () => {
        if (!targetUrl || !scheduledTime) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            let formattedUrl = targetUrl.trim();
            if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
                formattedUrl = 'https://' + formattedUrl;
            }

            // Ensure scheduledTime is a valid Date object
            const scheduleDate = new Date(scheduledTime);
            if (isNaN(scheduleDate.getTime())) {
                throw new Error('Invalid date');
            }

            // Format date in YYYYMMDDTHHmmss format
            const year = scheduleDate.getFullYear();
            const month = String(scheduleDate.getMonth() + 1).padStart(2, '0');
            const day = String(scheduleDate.getDate()).padStart(2, '0');
            const hours = String(scheduleDate.getHours()).padStart(2, '0');
            const minutes = String(scheduleDate.getMinutes()).padStart(2, '0');
            const seconds = String(scheduleDate.getSeconds()).padStart(2, '0');
            const formattedDate = `${year}${month}${day}T${hours}${minutes}${seconds}`;

            const response = await scanApi.startScan({
                targetUrl: formattedUrl,
                scheduledTime: formattedDate
            });

            if (response?.scanId) {
                const formattedDisplayDate = scheduleDate.toLocaleString();
                setSuccess(`Scan scheduled for ${formattedDisplayDate}`);
                setShowScheduleDialog(false);
                setScheduledTime(null);
            }
        } catch (error) {
            console.error('Schedule creation failed:', error);
            setError('Failed to schedule scan: ' + (error.message || 'Unknown error'));
        }
    };

    // Add the loading overlay component
    const renderLoadingOverlay = () => {
        const shouldShowResume = isResuming && (!currentScan || currentScan?.status !== 'running');
        
        return (
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    flexDirection: 'column',
                    gap: 2
                }}
                open={shouldShowResume}
            >
                <CircularProgress color="inherit" />
                <Typography variant="h6">
                    {currentScan?.status === 'running' ? 'Loading Scan Data...' : 'Resuming Scan...'}
                </Typography>
                <Typography variant="body2" color="inherit">
                    Please wait while we load the scan data
                </Typography>
            </Backdrop>
        );
    };

    // Update the render method to include the loading overlay
    return (
        <>
            {renderLoadingOverlay()}
            {renderContent()}
            {renderScheduleDialog()}
            
            <Dialog 
                open={showDomainVerificationModal} 
                onClose={() => setShowDomainVerificationModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Domain Verification Required</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        The domain {domainToVerify ? new URL(domainToVerify).hostname : ''} needs to be verified before scanning.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please go to the Domain Verification page to complete the verification process.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDomainVerificationModal(false)}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={() => {
                            setShowDomainVerificationModal(false);
                            window.location.href = '/domains';
                        }}
                    >
                        Go to Domain Verification
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// eslint-disable-next-line no-unused-vars
function debounce(func, wait) {
    let timeout;
    const debouncedFunc = function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
    debouncedFunc.cancel = () => clearTimeout(timeout);
    return debouncedFunc;
}

export default Scanner;
